import is from '@sindresorhus/is';
import { z } from 'zod';

export const LEARNING_OPPORTUNITY_ENTITY_TYPE = 'learning_opportunity';

export const entityTypeSchema = z.enum([
    'family',
    'cluster',
    'role',
    'level',
    'competency',
    'skill',
    'job_architecture',
    'leveling_framework',
    'notification',
    'user',
    // Settings agent entities. Their `id` is the tenant, user, or invitation UUID; none
    // carry a job architecture context.
    'company_profile',
    'company_settings',
    'tenant_member',
    'invitation',
    // Skill catalog agent: the tenant-wide catalog page, no id and no job architecture.
    'skill_catalog',
    // Job profile template agent: refresh-target only. The id is the template UUID and
    // jobArchitectureId names the architecture whose template changed.
    'job_profile_template',
    // Learning opportunity agent: `id` is the learning opportunity UUID; no job architecture.
    LEARNING_OPPORTUNITY_ENTITY_TYPE,
    // Position agent: `id` is the position or position architecture UUID; no job architecture.
    'position',
    'position_architecture',
    'alignment',
]);

export type EntityType = z.infer<typeof entityTypeSchema>;

const uuidSchema = z.uuid();

export const entityIdentifierSchema = z
    .object({
        clusterId: z.uuid().nullable().optional(),
        familyId: z.uuid().nullable().optional(),
        id: z.string().nullable().optional(),
        jobArchitectureId: z.uuid().nullable().optional(),
        levelId: z.uuid().nullable().optional(),
        roleId: z.uuid().nullable().optional(),
        skillType: z.enum(['hard', 'language', 'soft']).nullish(),
        title: z.string().nullish(),
        type: entityTypeSchema,
        version: z.number().nullable().optional(),
    })
    .superRefine((entity, context) => {
        if (is.nullOrUndefined(entity.id) || entity.type === 'alignment') return;
        if (!uuidSchema.safeParse(entity.id).success) {
            context.addIssue({ code: 'custom', message: 'Invalid UUID', path: ['id'] });
        }
    });

export type EntityIdentifier = z.infer<typeof entityIdentifierSchema>;
